// ICT-2 Proxy Material Inbox Audit — today's proxymaterial@ mail that reached
// nobody who will act on it: no ICT/Collections team member, documents@,
// verifications@ or notifications@. Ace decides where each one should go; a
// person checks, changes it if needed, and forwards. Nothing is ever deleted
// — the mailbox is the repository.
import { classifyEmail } from './proxyEmailClassifier';

export const PROXY_MATERIAL_INBOX = 'proxymaterial@betanxt.com';

const DESK_MAILBOXES = ['documents@betanxt.com', 'verifications@betanxt.com', 'notifications@betanxt.com'];
const ICT_TEAM = [
  'ict@betanxt.com', 'ict-manager@betanxt.com', 'ict-rdq@betanxt.com',
  'ict-billing@betanxt.com', 'ict-contest@betanxt.com', 'ict-credit@betanxt.com',
];

export type AuditTarget = 'documents' | 'pdf' | 'manager';

export const AUDIT_TARGETS: Record<AuditTarget, { label: string; to: string; cc: string[] }> = {
  documents: { label: 'documents@ (job setup change)', to: 'documents@betanxt.com', cc: [] },
  pdf: { label: 'documents@ + verifications@ (material PDF)', to: 'documents@betanxt.com', cc: ['verifications@betanxt.com'] },
  manager: { label: 'ICT manager (outside the audit rules)', to: 'ict-manager@betanxt.com', cc: [] },
};

export interface AuditMessage {
  id: string;
  to: string;
  cc: string[];
  date: string;
  subject: string;
  body: string;
}

export interface AuditFinding {
  target: AuditTarget;
  why: string;
}

export function reachesActionParty(messages: { to: string; cc: string[] }[]): boolean {
  return messages.some(m => [m.to, ...m.cc].some(addr => DESK_MAILBOXES.includes(addr) || ICT_TEAM.includes(addr)));
}

// Null when the thread is out of scope (not today's proxymaterial@ mail) or
// already reached someone who will act on it.
export function auditThread(messages: AuditMessage[], today: string): AuditFinding | null {
  const original = messages[0];
  if (!original || original.to !== PROXY_MATERIAL_INBOX || original.date.slice(0, 10) !== today) return null;
  if (reachesActionParty(messages)) return null;
  const { category } = classifyEmail({ ...original, from: '', fromEmail: '' });
  // A bounce is already an Exceptions task (EMAIL_BOUNCE_ROUTE).
  if (category === 'BOUNCE') return null;
  if (category === 'JOB_CHANGE') return { target: 'documents', why: 'A job setup change (dates or mailing method). Discovery updates the job.' };
  if (category === 'MATERIAL_PDF') return { target: 'pdf', why: 'Material PDFs. Discovery and Verifications both need the file.' };
  return { target: 'manager', why: 'Not a job setup change or a material PDF, so ICT-2 sends it to the manager to review.' };
}
